package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.model.Donation
import com.dotdrives.model.DonationSmartDefaults
import com.dotdrives.model.GiverSummary
import com.dotdrives.model.NewDonation
import com.dotdrives.repository.DonationRepository
import com.dotdrives.repository.GiverRepository
import kotlin.time.Clock
import kotlin.time.Instant
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

data class QuickRecordUiState(
    val contextLocked: Boolean = false,
    val giverQuery: String = "",
    val giverResults: List<GiverSummary> = emptyList(),
    val searchingGivers: Boolean = false,
    val selectedGiver: GiverSummary? = null,
    val amountText: String = "",
    val defaults: DonationSmartDefaults? = null,
    val loadingDefaults: Boolean = false,
    val targetId: String? = null,
    val paymentType: String = "check",
    val memo: String = "",
    val moreOptionsOpen: Boolean = false,
    val saving: Boolean = false,
    val savedDonation: Donation? = null,
    val error: String? = null,
)

/**
 * The "two fields, smart defaults" quick-record dialog. Giver and amount are the only required
 * fields; target, payment type, and any drive/pledge match are prefilled from
 * [DonationSmartDefaults] and travel with the save silently unless "More options" is opened.
 */
class QuickRecordController(
    private val giverRepo: GiverRepository,
    private val donationRepo: DonationRepository,
    scope: CoroutineScope,
    private val orgId: String,
    private val clock: () -> Instant = { Clock.System.now() },
) : DotController<QuickRecordUiState>(QuickRecordUiState(), scope) {
    private var searchJob: Job? = null
    private var defaultsGeneration = 0L

    fun start() {
        searchJob?.cancel()
        update { QuickRecordUiState() }
    }

    fun startForDrive(drive: com.dotdrives.model.DriveSummary) =
        startDriveContext(
            drive.id,
            drive.title,
            drive.giverId,
            drive.giverDisplayName,
            drive.giverType,
            drive.targetId,
            drive.targetName,
        )

    fun startForDrive(drive: com.dotdrives.model.Drive) =
        startDriveContext(
            drive.id,
            drive.title,
            drive.giverId,
            drive.giverDisplayName,
            "unknown",
            drive.targetId,
            drive.targetName,
        )

    private fun startDriveContext(
        id: String,
        title: String,
        giverId: String,
        giverName: String,
        giverType: String,
        targetId: String?,
        targetName: String?,
    ) {
        start()
        nextDefaultsGeneration()
        update {
            QuickRecordUiState(
                contextLocked = true,
                selectedGiver =
                    GiverSummary(giverId, giverName, giverType, null, null, "unknown", emptyList()),
                defaults = DonationSmartDefaults(targetId, targetName, null, id, title, null, null),
                targetId = targetId,
                paymentType = "",
                moreOptionsOpen = true,
            )
        }
    }

    fun searchGiver(query: String) {
        if (state.value.contextLocked) return
        searchJob?.cancel()
        update { it.copy(giverQuery = query, error = null) }
        val trimmed = query.trim()
        if (trimmed.isEmpty()) {
            update { it.copy(giverResults = emptyList(), searchingGivers = false) }
            return
        }
        searchJob =
            scope.launch {
                delay(SEARCH_DEBOUNCE_MILLIS)
                update { it.copy(searchingGivers = true) }
                giverRepo
                    .list(orgId, trimmed, null, null, null, 1, GIVER_RESULTS_LIMIT)
                    .onSuccess { result ->
                        update { it.copy(giverResults = result.items, searchingGivers = false) }
                    }
                    .onFailure { error ->
                        update {
                            it.copy(
                                searchingGivers = false,
                                error = error.message ?: "Something went wrong",
                            )
                        }
                    }
            }
    }

    fun selectGiver(giver: GiverSummary) {
        if (state.value.contextLocked) return
        val gen = nextDefaultsGeneration()
        update {
            it.copy(
                selectedGiver = giver,
                giverQuery = giver.displayName,
                giverResults = emptyList(),
                loadingDefaults = true,
                defaults = null,
                error = null,
            )
        }
        scope.launch {
            donationRepo
                .smartDefaults(orgId, giver.id)
                .onSuccess { defaults ->
                    if (!isCurrentDefaults(gen)) return@onSuccess
                    update {
                        if (isCurrentDefaults(gen)) {
                            it.copy(
                                loadingDefaults = false,
                                defaults = defaults,
                                targetId = defaults.targetId ?: it.targetId,
                                paymentType = defaults.paymentType ?: it.paymentType,
                            )
                        } else {
                            it
                        }
                    }
                }
                .onFailure { error ->
                    if (!isCurrentDefaults(gen)) return@onFailure
                    update { if (isCurrentDefaults(gen)) it.copy(loadingDefaults = false) else it }
                }
        }
    }

    fun setAmount(text: String) {
        update { it.copy(amountText = text, error = null) }
    }

    fun setTargetId(id: String?) {
        update { it.copy(targetId = id, error = null) }
    }

    fun setPaymentType(paymentType: String) {
        update { it.copy(paymentType = paymentType, error = null) }
    }

    fun setMemo(memo: String) {
        update { it.copy(memo = memo) }
    }

    fun toggleMoreOptions() {
        update { it.copy(moreOptionsOpen = !it.moreOptionsOpen) }
    }

    fun validatedDonation(): NewDonation? {
        val current = state.value
        if (current.saving) return null
        val giver = current.selectedGiver
        if (giver == null) {
            update { it.copy(error = "Choose a giver") }
            return null
        }
        val amount = current.amountText.trim().replace(",", "").toDoubleOrNull()
        if (amount == null || !amount.isFinite() || amount <= 0.0 || amount >= 10_000_000_000.0 ||
            kotlin.math.abs(amount * 100 - kotlin.math.round(amount * 100)) > 0.000001) {
            update { it.copy(error = "Enter a positive amount with at most two decimal places") }
            return null
        }
        if (current.paymentType !in setOf("check", "cash", "card", "ach", "wire", "other")) {
            update { it.copy(error = "Choose how the gift was received") }
            return null
        }
        return NewDonation(
            giverId = giver.id,
            amount = amount,
            driveId = current.defaults?.driveId,
            pledgeId = current.defaults?.pledgeId,
            targetId = current.targetId,
            paymentType = current.paymentType,
            source = "manual",
            memo = current.memo.trim(),
            occurredAt = clock(),
        )
    }

    fun save() {
        val newDonation = validatedDonation() ?: return
        update { it.copy(saving = true, error = null, savedDonation = null) }
        scope.launch {
            donationRepo
                .record(orgId, newDonation)
                .onSuccess { saved ->
                    update { it.copy(saving = false, savedDonation = saved, error = null) }
                }
                .onFailure { error ->
                    update {
                        it.copy(saving = false, error = error.message ?: "Something went wrong")
                    }
                }
        }
    }

    private fun nextDefaultsGeneration(): Long {
        defaultsGeneration += 1
        return defaultsGeneration
    }

    private fun isCurrentDefaults(generation: Long): Boolean = generation == defaultsGeneration
}

private const val SEARCH_DEBOUNCE_MILLIS = 250L
private const val GIVER_RESULTS_LIMIT = 6
